import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { appeler } from '../../api/client.ts';
import { Erreur } from '../../composants/communs.tsx';
import { Sheet } from '../../composants/ui/index.ts';
import type { Produit, Referentiel } from './types.ts';

/**
 * Identifiant SERVEUR du produit.
 *
 * La liste du catalogue renvoie un identifiant par CANAL : le même produit y
 * figure une fois au détail, une fois au gros. La modification, elle, porte
 * sur le produit — l'envoyer avec le canal collé ferait répondre 404.
 */
function produitServeur(produit: Produit): string {
  return produit.id.split('|')[0] ?? produit.id;
}

/**
 * Modifier un produit — désignation, catégorie, unité.
 *
 * Rien ne permettait de corriger une fiche après coup : une désignation mal
 * orthographiée ou une catégorie choisie par erreur restait figée à vie,
 * alors que `PATCH /produits/:id` l'accepte depuis le début côté serveur.
 *
 * Catégorie et unité ne sont PRÉ-REMPLIES que pour qui détient
 * `produit.modifier` (RG-038) — c'est justement le cas ici, cette action
 * n'étant proposée qu'à eux.
 */
export function FormulaireModifierProduit({
  produit,
  onFerme,
  onEnregistre,
}: {
  produit: Produit;
  onFerme: () => void;
  onEnregistre: () => void;
}): ReactNode {
  const [designation, setDesignation] = useState(produit.designation);
  const [categorieId, setCategorieId] = useState(produit.categorieId ?? '');
  const [uniteId, setUniteId] = useState(produit.uniteBaseId ?? '');

  const { data: referentiel } = useQuery({
    queryKey: ['referentiel-catalogue'],
    queryFn: () => appeler<Referentiel>('/referentiel/catalogue'),
  });

  const mutation = useMutation({
    mutationFn: () =>
      appeler(`/produits/${produitServeur(produit)}`, {
        methode: 'PATCH',
        corps: {
          designation: designation.trim(),
          categorie_id: categorieId,
          unite_base_id: uniteId,
        },
      }),
    onSuccess: () => {
      onEnregistre();
      onFerme();
    },
  });

  const valide = designation.trim() !== '' && categorieId !== '' && uniteId !== '';

  return (
    <Sheet titre={`Modifier — ${produit.designation}`}>
      <form
        onSubmit={(evenement: SyntheticEvent) => {
          evenement.preventDefault();
          if (valide) {
            mutation.mutate();
          }
        }}
      >
        <div className="champ">
          <label htmlFor="modif-designation">Désignation</label>
          <input
            id="modif-designation"
            value={designation}
            onChange={(evenement) => {
              setDesignation(evenement.target.value);
            }}
          />
        </div>

        <div className="champ">
          <label htmlFor="modif-categorie">Catégorie</label>
          <select
            id="modif-categorie"
            value={categorieId}
            onChange={(evenement) => {
              setCategorieId(evenement.target.value);
            }}
          >
            <option value="">Choisir…</option>
            {(referentiel?.categories ?? []).map((categorie) => (
              <option key={categorie.id} value={categorie.id}>
                {categorie.libelle}
              </option>
            ))}
          </select>
        </div>

        <div className="champ">
          <label htmlFor="modif-unite">Unité de base</label>
          <select
            id="modif-unite"
            value={uniteId}
            onChange={(evenement) => {
              setUniteId(evenement.target.value);
            }}
          >
            <option value="">Choisir…</option>
            {(referentiel?.unites ?? []).map((unite) => (
              <option key={unite.id} value={unite.id}>
                {unite.libelle}
              </option>
            ))}
          </select>
        </div>

        <Erreur erreur={mutation.error} />

        <div className="ligne">
          <button type="submit" disabled={!valide || mutation.isPending}>
            {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button type="button" className="bouton--discret" onClick={onFerme}>
            Annuler
          </button>
        </div>
      </form>
    </Sheet>
  );
}
